import React, { memo, useCallback, useRef } from 'react';
import { format } from 'date-fns';
import { View, StyleSheet, } from 'react-native';
import { useAppSelector } from 'store/hooks';
import { focus, useAutoFocusInput } from 'hooks/useAutoFocusInput';
import Date from './DateInputField';
const DateInput = ({ state, onChange, onSubmitEditing, showYearError, onDark, dateOfBirth, }) => {
    const dayRef = useRef(null);
    const monthRef = useRef(null);
    const yearRef = useRef(null);
    const guessedHomeCountry = useAppSelector((store) => store.user.user.guessedHomeCountry);
    const monthFirst = guessedHomeCountry === 'US' || guessedHomeCountry === 'CA';
    const previousButtonRef = useRef('');
    const onChangeDay = useCallback((data) => {
        const parsedText = data.replace(/[^0-9]/g, '');
        if (parsedText === state.day || parsedText === '00')
            return;
        if (Number(parsedText) > 31 || parsedText.length > 2) {
            focus(monthFirst ? yearRef : monthRef);
            return;
        }
        const newState = { ...state, day: parsedText };
        onChange(newState);
        if (previousButtonRef.current === 'Backspace')
            return;
        if (Number(parsedText.charAt(0)) > 3 || parsedText.length >= 2) {
            focus(monthFirst ? yearRef : monthRef);
        }
    }, [monthFirst, onChange, state]);
    const onDayKeyPress = useCallback((data) => {
        previousButtonRef.current = data.nativeEvent.key;
        if (!state.day && data.nativeEvent.key === 'Backspace' && monthFirst) {
            focus(monthRef);
        }
    }, [monthFirst, state.day]);
    const onChangeMonth = useCallback((data) => {
        const parsedText = data.replace(/[^0-9]/g, '');
        if (parsedText === state.month || parsedText === '00')
            return;
        if (Number(parsedText) > 12 || parsedText.length > 2) {
            focus(monthFirst ? dayRef : yearRef);
            return;
        }
        const newState = { ...state, month: parsedText };
        onChange(newState);
        if (previousButtonRef.current === 'Backspace')
            return;
        if (Number(parsedText.charAt(0)) > 1 || parsedText.length >= 2) {
            focus(monthFirst ? dayRef : yearRef);
        }
    }, [monthFirst, onChange, state]);
    const onMonthKeyPress = useCallback((data) => {
        previousButtonRef.current = data.nativeEvent.key;
        if (!state.month && data.nativeEvent.key === 'Backspace' && !monthFirst) {
            focus(dayRef);
        }
    }, [monthFirst, state.month]);
    const onChangeYear = useCallback((data) => {
        const parsedText = data.replace(/[^0-9]/g, '');
        if (parsedText === state.year || parsedText.length > 4)
            return;
        const newState = { ...state, year: parsedText };
        onChange(newState);
    }, [onChange, state]);
    const onYearKeyPress = useCallback((data) => {
        previousButtonRef.current = data.nativeEvent.key;
        if (!state.year && data.nativeEvent.key === 'Backspace') {
            focus(monthFirst ? dayRef : monthRef);
        }
    }, [monthFirst, state.year]);
    useAutoFocusInput(monthFirst ? monthRef : dayRef);
    return (<View style={styles.container}>
      <View style={[styles.container, monthFirst && styles.monthFirst]}>
        <Date ref={dayRef} title="Day" placeholder="DD" maxLength={2} value={state.day} onChangeText={onChangeDay} onKeyPress={onDayKeyPress} onDark={onDark} autoComplete={dateOfBirth ? 'birthdate-day' : undefined}/>
        <Date ref={monthRef} title="Month" placeholder="MM" maxLength={2} value={state.month} onKeyPress={onMonthKeyPress} onChangeText={onChangeMonth} onDark={onDark} autoComplete={dateOfBirth ? 'birthdate-month' : undefined}/>
      </View>
      <Date ref={yearRef} title="Year" placeholder="YYYY" maxLength={4} value={state.year} onChangeText={onChangeYear} onKeyPress={onYearKeyPress} onSubmitEditing={onSubmitEditing} error={showYearError} onDark={onDark} autoComplete={dateOfBirth ? 'birthdate-year' : undefined}/>
    </View>);
};
export const toDate = (time) => ({
    day: time ? format(time, 'd') : undefined,
    month: time ? format(time, 'M') : undefined,
    year: time ? format(time, 'yyyy') : undefined,
});
const styles = StyleSheet.create({
    container: {
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'row',
    },
    monthFirst: {
        flexDirection: 'row-reverse',
    },
});
export default memo(DateInput);
